import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/agent-tasks`;

interface Task {
  id: number; instruction: string; task_type: string; entity_code: string;
  status: string; progress_pct: number; progress_message: string;
  current_step: string; total_steps: number; completed_steps: number;
  items_processed: number; items_succeeded: number; items_failed: number; items_skipped: number;
  result: any; error: string; started_at: string; completed_at: string; created_at: string;
  created_by: number; agent_user_id: number;
}

export default function AgentTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [instruction, setInstruction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    try {
      const res = await authFetch(`${API}?${p}`);
      if (res.ok) { const d = await res.json(); setTasks(d.items || []); setTotal(d.total || 0); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!instruction.trim()) return;
    setSubmitting(true);
    try {
      // Create and execute immediately
      const res = await authFetch(`${API}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: instruction.trim() }),
      });
      if (res.ok) {
        const task = await res.json();
        if (task.status === 'PENDING') {
          // Execute
          const execRes = await authFetch(`${API}/${task.id}/execute`, { method: 'POST' });
          if (execRes.ok) {
            const result = await execRes.json();
            showToast(`Task completed: ${result.items_succeeded || 0} items processed`, true);
          }
        } else if (task.status === 'FAILED') {
          showToast(task.error || 'Task failed', false);
        }
        setInstruction('');
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Failed to create task', false);
      }
    } catch { showToast('Failed', false); }
    setSubmitting(false);
  };

  const cancelTask = async (id: number) => {
    try {
      const res = await authFetch(`${API}/${id}/cancel`, { method: 'POST' });
      if (res.ok) { showToast('Task cancelled', true); load(); }
    } catch { showToast('Cancel failed', false); }
  };

  const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap', padding: '10px 14px' };
  const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };
  const labelStyle: React.CSSProperties = { fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 2 };

  const completed = tasks.filter(t => t.status === 'COMPLETED').length;
  const failed = tasks.filter(t => t.status === 'FAILED').length;
  const running = tasks.filter(t => t.status === 'EXECUTING').length;
  const totalProcessed = tasks.reduce((s, t) => s + (t.items_succeeded || 0), 0);

  return (
    <PageLayout title="Agent Tasks" subtitle="Give natural language instructions — AI agent executes autonomously within DOA limits">

      {/* Instruction Input */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          value={instruction}
          onChange={e => setInstruction(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
          placeholder='Type an instruction... e.g., "Approve all pending invoices" or "Run month-end close for entity 1000"'
          style={{ flex: 1, padding: '12px 16px', border: '2px solid var(--border-default)', borderRadius: 8, fontSize: 14, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
          disabled={submitting}
        />
        <ActionButton label={submitting ? 'Processing...' : 'Execute'} onClick={handleSubmit} disabled={submitting || !instruction.trim()} />
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          'Approve all pending invoices',
          'Process all received invoices',
          'Post all approved invoices',
          'Generate AP aging report',
          'Post pending journal entries',
        ].map(q => (
          <button key={q} onClick={() => setInstruction(q)} style={{ padding: '6px 12px', borderRadius: 16, border: '1px solid var(--border-default)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>{q}</button>
        ))}
      </div>

      <KpiGrid items={[
        { label: 'Total Tasks', value: total, color: 'var(--text-primary)' },
        { label: 'Running', value: running, color: 'var(--warning)' },
        { label: 'Completed', value: completed, color: 'var(--positive)' },
        { label: 'Failed', value: failed, color: 'var(--negative)' },
        { label: 'Items Processed', value: totalProcessed, color: 'var(--accent)' },
      ]} />

      <FilterBar filters={[
        { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: v => setStatusFilter(v), options: ['PENDING', 'PARSING', 'EXECUTING', 'COMPLETED', 'FAILED', 'CANCELLED'].map(s => ({ value: s, label: s })), width: 180 },
      ]} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : tasks.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No tasks yet. Type an instruction above to create one.</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['ID', 'Instruction', 'Type', 'Status', 'Progress', 'Items', 'Time', 'Actions'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {tasks.map(task => (
                <>
                  <tr key={task.id} onClick={() => setExpandedId(expandedId === task.id ? null : task.id)} style={{ cursor: 'pointer', background: expandedId === task.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>#{task.id}</td>
                    <td style={{ ...tdStyle, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.instruction}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 11 }}>{task.task_type || '-'}</td>
                    <td style={tdStyle}><StatusBadge status={task.status} /></td>
                    <td style={tdStyle}>
                      {task.status === 'EXECUTING' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 60, height: 6, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${task.progress_pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11 }}>{task.progress_pct}%</span>
                        </div>
                      ) : task.status === 'COMPLETED' ? '100%' : '-'}
                    </td>
                    <td style={tdStyle}>
                      {task.items_processed > 0 ? (
                        <span style={{ fontSize: 12 }}>
                          <span style={{ color: 'var(--positive)' }}>{task.items_succeeded}</span>
                          {task.items_failed > 0 && <span style={{ color: 'var(--negative)' }}> / {task.items_failed}F</span>}
                          {task.items_skipped > 0 && <span style={{ color: 'var(--warning)' }}> / {task.items_skipped}S</span>}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-secondary)' }}>
                      {task.completed_at ? `${((new Date(task.completed_at).getTime() - new Date(task.started_at || task.created_at).getTime()) / 1000).toFixed(1)}s` : task.started_at ? 'Running...' : '-'}
                    </td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      {task.status === 'PENDING' && <ActionButton label="Execute" onClick={() => { authFetch(`${API}/${task.id}/execute`, { method: 'POST' }).then(() => load()); }} variant="primary" size="sm" />}
                      {['PENDING', 'EXECUTING'].includes(task.status) && <ActionButton label="Cancel" onClick={() => cancelTask(task.id)} variant="secondary" size="sm" />}
                    </td>
                  </tr>

                  {expandedId === task.id && (
                    <tr key={`${task.id}-detail`}><td colSpan={8} style={{ padding: '0 14px 16px 14px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                          {[
                            { label: 'Instruction', value: task.instruction },
                            { label: 'Task Type', value: task.task_type || 'Parsing...' },
                            { label: 'Entity', value: task.entity_code || 'All' },
                            { label: 'Current Step', value: task.current_step || '-' },
                          ].map(f => (
                            <div key={f.label} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                              <div style={labelStyle}>{f.label}</div>
                              <div style={{ fontSize: 12 }}>{f.value}</div>
                            </div>
                          ))}
                        </div>

                        {task.error && (
                          <div style={{ padding: 10, borderRadius: 6, background: '#fef2f2', border: '1px solid #ef4444' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>Error: {task.error}</div>
                          </div>
                        )}

                        {task.result && (
                          <div style={{ padding: 10, borderRadius: 6, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', marginBottom: 4 }}>RESULT</div>
                            <pre style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0, maxHeight: 200, overflow: 'auto' }}>
                              {JSON.stringify(task.result, null, 2)}
                            </pre>
                          </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                          {[
                            { label: 'Processed', value: task.items_processed, color: 'var(--text-primary)' },
                            { label: 'Succeeded', value: task.items_succeeded, color: 'var(--positive)' },
                            { label: 'Failed', value: task.items_failed, color: 'var(--negative)' },
                            { label: 'Skipped', value: task.items_skipped, color: 'var(--warning)' },
                            { label: 'Duration', value: task.completed_at && task.started_at ? `${((new Date(task.completed_at).getTime() - new Date(task.started_at).getTime()) / 1000).toFixed(1)}s` : '-', color: 'var(--text-secondary)' },
                          ].map(k => (
                            <div key={k.label} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' }}>{k.label}</div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: k.color }}>{k.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
